/**
 * JourneyTimeline — horizontal timeline showing a contact's journey from ad click to purchase.
 * Pure CSS-based visualization with no external chart dependencies.
 */

import { useState } from 'react'
import {
  Eye,
  MousePointerClick,
  MessageCircle,
  UserCheck,
  ShoppingCart,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { formatMarketingCents } from '../format'

export interface JourneyEvent {
  type: 'ad_impression' | 'ad_click' | 'wa_conversation' | 'lead' | 'purchase'
  label: string
  date: string
  details: Record<string, string | number>
  /** Monetary value in integer cents (deployment default currency). */
  value?: number
}

export interface JourneyTimelineProps {
  events: JourneyEvent[]
  height?: number
  onEventClick?: (event: JourneyEvent, index: number) => void
}

interface EventStyle {
  icon: LucideIcon
  color: string
  bg: string
  border: string
  ring: string
  text: string
  line: string
}

const eventStyles: Record<JourneyEvent['type'], EventStyle> = {
  ad_impression: {
    icon: Eye,
    color: 'text-gray-500',
    bg: 'bg-gray-100 dark:bg-gray-700',
    border: 'border-gray-300 dark:border-gray-500',
    ring: 'ring-gray-200 dark:ring-gray-600',
    text: 'text-gray-700 dark:text-gray-300',
    line: 'bg-gray-300 dark:bg-gray-500',
  },
  ad_click: {
    icon: MousePointerClick,
    color: 'text-blue-500',
    bg: 'bg-blue-50 dark:bg-blue-900/30',
    border: 'border-blue-300 dark:border-blue-500',
    ring: 'ring-blue-200 dark:ring-blue-700',
    text: 'text-blue-700 dark:text-blue-300',
    line: 'bg-blue-300 dark:bg-blue-500',
  },
  wa_conversation: {
    icon: MessageCircle,
    color: 'text-green-500',
    bg: 'bg-green-50 dark:bg-green-900/30',
    border: 'border-green-300 dark:border-green-500',
    ring: 'ring-green-200 dark:ring-green-700',
    text: 'text-green-700 dark:text-green-300',
    line: 'bg-green-300 dark:bg-green-500',
  },
  lead: {
    icon: UserCheck,
    color: 'text-amber-500',
    bg: 'bg-amber-50 dark:bg-amber-900/30',
    border: 'border-amber-300 dark:border-amber-500',
    ring: 'ring-amber-200 dark:ring-amber-700',
    text: 'text-amber-700 dark:text-amber-300',
    line: 'bg-amber-300 dark:bg-amber-500',
  },
  purchase: {
    icon: ShoppingCart,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50 dark:bg-emerald-900/30',
    border: 'border-emerald-400 dark:border-emerald-500',
    ring: 'ring-emerald-200 dark:ring-emerald-700',
    text: 'text-emerald-700 dark:text-emerald-300',
    line: 'bg-emerald-400 dark:bg-emerald-500',
  },
}

const formatValue = formatMarketingCents

export function JourneyTimeline({
  events,
  height = 300,
  onEventClick,
}: JourneyTimelineProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  if (events.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-sm text-muted-foreground"
        style={{ height }}
      >
        No journey events to display
      </div>
    )
  }

  const handleNodeClick = (event: JourneyEvent, index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index)
    onEventClick?.(event, index)
  }

  return (
    <div className="w-full overflow-x-auto" style={{ minHeight: height }}>
      {/* Horizontal layout (md+) */}
      <div className="hidden md:flex items-start justify-center px-4 py-6 min-w-fit">
        {events.map((event, index) => {
          const style = eventStyles[event.type]
          const Icon = style.icon
          const isExpanded = expandedIndex === index
          const isLast = index === events.length - 1

          return (
            <div key={index} className="flex items-start">
              {/* Node */}
              <div className="flex flex-col items-center" style={{ width: 140 }}>
                {/* Icon circle */}
                <button
                  onClick={() => handleNodeClick(event, index)}
                  className={`
                    relative flex items-center justify-center w-12 h-12 rounded-full
                    border-2 ${style.border} ${style.bg}
                    transition-all duration-200 cursor-pointer
                    hover:ring-4 ${style.ring}
                    ${isExpanded ? `ring-4 ${style.ring}` : ''}
                  `}
                  title={event.label}
                >
                  <Icon className={`w-5 h-5 ${style.color}`} />
                  {event.type === 'purchase' && event.value != null && (
                    <span className="absolute -top-2 -right-2 text-[10px] font-semibold bg-emerald-500 text-white px-1.5 py-0.5 rounded-full whitespace-nowrap">
                      {formatValue(event.value)}
                    </span>
                  )}
                </button>

                {/* Label and date */}
                <div className="mt-2 text-center">
                  <p className={`text-xs font-medium ${style.text} truncate max-w-[130px]`}>
                    {event.label}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {event.date}
                  </p>
                </div>

                {/* Details panel */}
                {isExpanded && Object.keys(event.details).length > 0 && (
                  <div className={`mt-2 p-2 rounded-lg ${style.bg} border ${style.border} text-xs w-full`}>
                    {Object.entries(event.details).map(([key, val]) => (
                      <div key={key} className="flex justify-between gap-2 py-0.5">
                        <span className="text-muted-foreground capitalize">
                          {key.replace(/_/g, ' ')}
                        </span>
                        <span className={`font-medium ${style.text}`}>
                          {val}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Connecting line */}
              {!isLast && (
                <div className="flex items-center mt-5">
                  <div className={`h-0.5 w-8 ${style.line}`} />
                  <div className="w-0 h-0 border-t-4 border-t-transparent border-b-4 border-b-transparent border-l-[6px] border-l-border" />
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Vertical layout (small screens) */}
      <div className="flex md:hidden flex-col items-start px-4 py-4 gap-0">
        {events.map((event, index) => {
          const style = eventStyles[event.type]
          const Icon = style.icon
          const isExpanded = expandedIndex === index
          const isLast = index === events.length - 1

          return (
            <div key={index} className="flex items-stretch">
              {/* Left rail: icon + line */}
              <div className="flex flex-col items-center mr-3">
                <button
                  onClick={() => handleNodeClick(event, index)}
                  className={`
                    relative flex items-center justify-center w-10 h-10 rounded-full
                    border-2 ${style.border} ${style.bg}
                    transition-all duration-200 cursor-pointer
                    hover:ring-4 ${style.ring}
                    ${isExpanded ? `ring-4 ${style.ring}` : ''}
                  `}
                  title={event.label}
                >
                  <Icon className={`w-4 h-4 ${style.color}`} />
                </button>
                {!isLast && (
                  <div className={`w-0.5 flex-1 min-h-[24px] ${style.line}`} />
                )}
              </div>

              {/* Right: content */}
              <div className="pb-4 pt-1 flex-1 min-w-0">
                <p className={`text-sm font-medium ${style.text}`}>
                  {event.label}
                </p>
                <p className="text-[11px] text-muted-foreground">{event.date}</p>
                {event.type === 'purchase' && event.value != null && (
                  <span className="inline-block mt-1 text-xs font-semibold bg-emerald-500 text-white px-2 py-0.5 rounded-full">
                    {formatValue(event.value)}
                  </span>
                )}

                {isExpanded && Object.keys(event.details).length > 0 && (
                  <div className={`mt-2 p-2 rounded-lg ${style.bg} border ${style.border} text-xs`}>
                    {Object.entries(event.details).map(([key, val]) => (
                      <div key={key} className="flex justify-between gap-2 py-0.5">
                        <span className="text-muted-foreground capitalize">
                          {key.replace(/_/g, ' ')}
                        </span>
                        <span className={`font-medium ${style.text}`}>
                          {val}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
